/**
 * The two charts on the Trends page, drawn as plain SVG.
 *
 * A charting library would be the largest thing in the bundle, kept for a
 * stacked bar and a line. These are a few rectangles and one path, sized from
 * the width of the card they sit in.
 *
 * Both are read three ways, and all three are served:
 *   - a mouse hovers for the numbers and clicks a bar to open what is behind it;
 *   - a finger has no hover, so the first tap shows the numbers and the second
 *     opens them;
 *   - a keyboard tabs through the bars and presses Enter.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { formatDate, formatMoney } from './ui.js';
import { NONE_SLOT, OTHER_SLOT, niceAxis, timeTicks, type BalancePoint, type Measure, type Period, type Split, type Stack } from './trends.js';

export function seriesColor(slot: number): string {
  if (slot >= NONE_SLOT) return 'var(--s-none)';
  if (slot >= OTHER_SLOT) return 'var(--s-other)';
  return `var(--s${slot + 1})`;
}

const plural = (n: number, word: string): string => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;

/** The width of the box a chart is drawn in, kept current as the window changes. */
function useWidth(): [RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/**
 * The numbers for one bar. It sits beside the bar, so the bar being read is
 * not the thing covered; on a phone there is no room beside, and it goes above.
 */
function Tip({ at, children }: { at: { x: number; y: number; half: number } | null; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const tip = ref.current;
    const host = tip?.parentElement;
    if (!tip || !host || !at) return;
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const W = host.clientWidth;
    let left = at.x + at.half + 10;
    if (left + w > W) left = at.x - at.half - 10 - w;
    if (left >= 0) {
      tip.style.left = `${left}px`;
      tip.style.top = `${Math.max(0, Math.min(host.clientHeight - h - 44, at.y - 8))}px`;
    } else {
      tip.style.left = `${Math.max(0, Math.min(W - w, at.x - w / 2))}px`;
      tip.style.top = `${at.y - h - 10}px`;
    }
  });
  if (!at) return null;
  return (
    <div className="tip" ref={ref} role="status">
      {children}
    </div>
  );
}

function axisLabels(axis: { max: number; step: number }, money: boolean): { value: number; text: string }[] {
  const out: { value: number; text: string }[] = [];
  for (let v = 0; v <= axis.max + axis.step / 1000; v += axis.step) {
    const text = axis.step < 1 ? v.toFixed(2) : Math.round(v).toLocaleString('en-US');
    out.push({ value: v, text: money ? `$${text}` : text });
  }
  return out;
}

/** Room for the widest label on the left, so "$12,000" is never clipped. */
const gutter = (labels: { text: string }[]): number => Math.max(30, 12 + 6.9 * Math.max(...labels.map((l) => l.text.length)));

function XLabels({ period, x, step, y }: { period: Period; x: (k: number) => number; step: number; y: number }) {
  // Twelve months do not fit across a phone as "Jan", "Feb", …; initials do.
  const initials = period.unit === 'month' && step < 30;
  return (
    <>
      {period.buckets.map((b, k) => (
        <g key={b.title}>
          <text className="axis" x={x(k)} y={y + 17} textAnchor="middle">
            {initials ? b.label[0] : b.label}
          </text>
          {b.year ? (
            <text className="axis year" x={x(k)} y={y + 33} textAnchor="middle">
              {b.year}
            </text>
          ) : null}
        </g>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Stacked bars: what was paid, or how many, in each slice of time
// ---------------------------------------------------------------------------

export function BarChart({
  period,
  split,
  data,
  measure,
  label,
  onPick,
}: {
  period: Period;
  split: Split;
  data: Stack[];
  measure: Measure;
  label: string;
  /** Open the records behind a bar. */
  onPick: (bucket: number) => void;
}) {
  const [host, W] = useWidth();
  const [shown, setShown] = useState<{ k: number; by: 'hover' | 'tap' } | null>(null);
  const finger = useRef(false);
  // A different period has different bars; a pinned one would point at nothing.
  useEffect(() => setShown(null), [period.id, period.buckets.length]);

  const amount = measure === 'amount';
  const value = (d: Stack, s: number): number => (amount ? (d.cents[s] ?? 0) / 100 : (d.count[s] ?? 0));
  const totalOf = (d: Stack): number => split.list.reduce((t, _, s) => t + value(d, s), 0);
  const axis = niceAxis(Math.max(...data.map(totalOf)), !amount);
  const labels = axisLabels(axis, amount);

  const H = W < 520 ? 230 : 290;
  const M = { l: gutter(labels), r: 4, t: 10, b: 40 };
  const iw = Math.max(0, W - M.l - M.r);
  const ih = H - M.t - M.b;
  const step = iw / Math.max(1, data.length);
  const bw = Math.min(48, step * 0.64);
  const cx = (k: number): number => M.l + step * k + step / 2;
  const y = (v: number): number => M.t + ih - (v / axis.max) * ih;

  const current = shown && data[shown.k] ? { ...shown, d: data[shown.k]!, b: period.buckets[shown.k]! } : null;

  return (
    <div className="chart" ref={host}>
      {W > 0 ? (
        <svg className={current ? 'dim' : undefined} width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="group" aria-label={label}>
          {labels.map((l) => (
            <g key={l.text}>
              <line className="grid" x1={M.l} x2={W - M.r} y1={y(l.value)} y2={y(l.value)} />
              <text className="axis" x={M.l - 8} y={y(l.value) + 4} textAnchor="end">
                {l.text}
              </text>
            </g>
          ))}

          {data.map((d, k) => {
            const b = period.buckets[k]!;
            const x0 = cx(k) - bw / 2;
            const top = split.list.reduce((last, _, s) => (value(d, s) > 0 ? s : last), -1);
            let base = y(0);
            const pieces = split.list.map((series, s) => {
              const h = (value(d, s) / axis.max) * ih;
              if (h <= 0) return null;
              const from = base;
              base -= h;
              const fill = seriesColor(series.slot);
              if (s === top) {
                const r = Math.min(4, bw / 2, h);
                return (
                  <path
                    key={s}
                    fill={fill}
                    d={`M${x0},${from}V${base + r}Q${x0},${base} ${x0 + r},${base}H${x0 + bw - r}Q${x0 + bw},${base} ${x0 + bw},${base + r}V${from}Z`}
                  />
                );
              }
              // A hairline of the card's colour between the pieces of a stack.
              const gap = h > 3 ? 1.5 : 0;
              return <rect key={s} fill={fill} x={x0} y={base + gap} width={bw} height={h - gap} />;
            });
            const count = d.count.reduce((t, n) => t + n, 0);
            const cents = d.cents.reduce((t, n) => t + n, 0);
            const empty = count === 0;
            return (
              <g key={b.title}>
                <g className={`bar${current?.k === k ? ' on' : ''}`} fillOpacity={b.partial ? 0.5 : undefined}>
                  {pieces}
                </g>
                <rect
                  className="hit"
                  x={M.l + step * k}
                  y={M.t}
                  width={step}
                  height={ih + M.b}
                  tabIndex={0}
                  role={empty ? 'img' : 'link'}
                  aria-label={`${b.title}: ${empty ? 'no expenses' : `${formatMoney(cents)}, ${plural(count, 'expense')}`}`}
                  onPointerDown={(e) => (finger.current = e.pointerType !== 'mouse')}
                  onPointerEnter={(e) => e.pointerType === 'mouse' && setShown({ k, by: 'hover' })}
                  onPointerLeave={(e) => e.pointerType === 'mouse' && setShown(null)}
                  onFocus={() => !finger.current && setShown({ k, by: 'hover' })}
                  onBlur={() => setShown(null)}
                  onClick={() => {
                    if (finger.current && !(shown?.k === k && shown.by === 'tap')) setShown({ k, by: 'tap' });
                    else if (!empty) onPick(k);
                  }}
                  onKeyDown={(e) => {
                    if (empty || (e.key !== 'Enter' && e.key !== ' ')) return;
                    e.preventDefault();
                    onPick(k);
                  }}
                />
              </g>
            );
          })}

          <XLabels period={period} x={cx} step={step} y={M.t + ih} />
        </svg>
      ) : null}

      <Tip at={current ? { x: cx(current.k), y: y(totalOf(current.d)), half: bw / 2 } : null}>
        {current ? (
          <>
            <div className="tip-title">
              {current.b.title}
              {current.b.partial ? <span className="muted"> so far</span> : null}
            </div>
            <div className="tip-total">
              {current.d.count.every((n) => n === 0)
                ? 'No expenses'
                : `${formatMoney(current.d.cents.reduce((t, n) => t + n, 0))} · ${plural(
                    current.d.count.reduce((t, n) => t + n, 0),
                    'expense',
                  )}`}
            </div>
            {split.list.length > 1
              ? split.list.map((series, s) =>
                  (current.d.count[s] ?? 0) > 0 ? (
                    <div className="tip-row" key={series.key === null ? 'rest' : `is:${series.key}`}>
                      <i style={{ background: seriesColor(series.slot) }} />
                      <span>{series.name}</span>
                      <span>{amount ? formatMoney(current.d.cents[s] ?? 0) : current.d.count[s]}</span>
                    </div>
                  ) : null,
                )
              : null}
            {current.by === 'tap' && current.d.count.some((n) => n > 0) ? (
              <div className="tip-hint">Tap the bar again to see these.</div>
            ) : null}
          </>
        ) : null}
      </Tip>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One line: ready to claim, as it stood at the end of each slice
// ---------------------------------------------------------------------------

export function LineChart({ period, points, label }: { period: Period; points: number[]; label: string }) {
  const [host, W] = useWidth();
  const [shown, setShown] = useState<number | null>(null);
  useEffect(() => setShown(null), [period.id, period.buckets.length]);

  const axis = niceAxis(Math.max(...points) / 100, false);
  const labels = axisLabels(axis, true);
  const H = W < 520 ? 190 : 220;
  const M = { l: gutter(labels), r: 14, t: 26, b: 40 };
  const iw = Math.max(0, W - M.l - M.r);
  const ih = H - M.t - M.b;
  const step = iw / Math.max(1, points.length);
  const cx = (k: number): number => M.l + step * k + step / 2;
  const y = (cents: number): number => M.t + ih - (cents / 100 / axis.max) * ih;
  const end = points.length - 1;
  const at = shown ?? end;
  const path = points.map((c, k) => `${k === 0 ? 'M' : 'L'}${cx(k)},${y(c)}`).join('');

  return (
    <div className="chart" ref={host}>
      {W > 0 && end >= 0 ? (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
          {labels.map((l) => (
            <g key={l.text}>
              <line className="grid" x1={M.l} x2={W - M.r} y1={y(l.value * 100)} y2={y(l.value * 100)} />
              <text className="axis" x={M.l - 8} y={y(l.value * 100) + 4} textAnchor="end">
                {l.text}
              </text>
            </g>
          ))}
          <path className="area" d={`${path}L${cx(end)},${y(0)}L${cx(0)},${y(0)}Z`} />
          <path className="line" d={path} />
          {shown !== null ? <line className="guide" x1={cx(at)} x2={cx(at)} y1={M.t} y2={M.t + ih} /> : null}
          <circle className="dot" cx={cx(at)} cy={y(points[at] ?? 0)} r={4.5} />
          {shown === null ? (
            <text className="end-label" x={cx(end) + 6} y={y(points[end] ?? 0) - 11} textAnchor="end">
              {formatMoney(points[end] ?? 0)}
            </text>
          ) : null}
          {period.buckets.map((b, k) => (
            <rect
              key={b.title}
              className="hit"
              x={M.l + step * k}
              y={M.t}
              width={step}
              height={ih + M.b}
              onPointerEnter={() => setShown(k)}
              onPointerLeave={(e) => e.pointerType === 'mouse' && setShown(null)}
            />
          ))}
          <XLabels period={period} x={cx} step={step} y={M.t + ih} />
        </svg>
      ) : null}

      <Tip at={shown !== null ? { x: cx(shown), y: y(points[shown] ?? 0), half: 6 } : null}>
        {shown !== null ? (
          <>
            <div className="tip-title">{period.buckets[shown]!.partial ? 'Today' : `End of ${period.buckets[shown]!.title}`}</div>
            <div className="tip-total">{formatMoney(points[shown] ?? 0)} ready to claim</div>
          </>
        ) : null}
      </Tip>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The HSA's balance over time: what went in, and the growth on top of it
// ---------------------------------------------------------------------------

/**
 * Readings are copied in at whatever dates they happen to be, so x is time
 * and the points are joined by straight lines: between two readings the line
 * is an estimate, and each dot is a figure the custodian actually showed.
 */
export function BalanceChart({ points, label }: { points: BalancePoint[]; label: string }) {
  const [host, W] = useWidth();
  const [shown, setShown] = useState<number | null>(null);

  const axis = niceAxis(Math.max(...points.map((p) => p.balance)) / 100, false);
  const labels = axisLabels(axis, true);
  const H = W < 520 ? 180 : 200;
  const M = { l: gutter(labels), r: 14, t: 14, b: 40 };
  const iw = Math.max(0, W - M.l - M.r);
  const ih = H - M.t - M.b;
  const first = points[0]!.day;
  const last = points[points.length - 1]!.day;
  const x = (day: number): number => M.l + ((day - first) / Math.max(1, last - first)) * iw;
  const y = (cents: number): number => M.t + ih - (cents / 100 / axis.max) * ih;
  const line = (value: (p: BalancePoint) => number): string => points.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p.day)},${y(value(p))}`).join('');
  const area = (value: (p: BalancePoint) => number): string => `${line(value)}L${x(last)},${y(0)}L${x(first)},${y(0)}Z`;
  const ticks = timeTicks(first, last);

  /** The reading nearest the pointer. */
  const nearest = (clientX: number, rect: DOMRect): number => {
    const day = first + ((clientX - rect.left - M.l) / Math.max(1, iw)) * (last - first);
    return points.reduce((best, p, k) => (Math.abs(p.day - day) < Math.abs(points[best]!.day - day) ? k : best), 0);
  };
  const p = shown !== null ? points[shown]! : null;

  return (
    <div className="chart" ref={host}>
      {W > 0 ? (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
          {labels.map((l) => (
            <g key={l.text}>
              <line className="grid" x1={M.l} x2={W - M.r} y1={y(l.value * 100)} y2={y(l.value * 100)} />
              <text className="axis" x={M.l - 8} y={y(l.value * 100) + 4} textAnchor="end">
                {l.text}
              </text>
            </g>
          ))}
          {/* Two bands, stacked: what went in, and the growth between it and the balance. */}
          <path className="hsa-area kept" d={area((q) => q.kept)} />
          <path
            className="hsa-area growth"
            d={`${line((q) => q.balance)}${[...points].reverse().map((q) => `L${x(q.day)},${y(q.kept)}`).join('')}Z`}
          />
          <path className="hsa-line" d={line((q) => q.balance)} />
          {p ? <line className="guide" x1={x(p.day)} x2={x(p.day)} y1={M.t} y2={M.t + ih} /> : null}
          {points.map((q, k) => (
            <circle key={q.as_of} className={`dot${shown === k ? ' on' : ''}`} cx={x(q.day)} cy={y(q.balance)} r={shown === k ? 4.5 : 3} />
          ))}
          {ticks.map((t) => (
            <g key={t.day}>
              <text className="axis" x={x(t.day)} y={M.t + ih + 17} textAnchor="middle">
                {t.label}
              </text>
              {t.year ? (
                <text className="axis year" x={x(t.day)} y={M.t + ih + 33} textAnchor="middle">
                  {t.year}
                </text>
              ) : null}
            </g>
          ))}
          <rect
            className="hit"
            x={M.l - 10}
            y={M.t}
            width={iw + 20}
            height={ih}
            onPointerMove={(e) => setShown(nearest(e.clientX, e.currentTarget.ownerSVGElement!.getBoundingClientRect()))}
            onPointerLeave={(e) => e.pointerType === 'mouse' && setShown(null)}
          />
        </svg>
      ) : null}

      <Tip at={p ? { x: x(p.day), y: y(p.balance), half: 6 } : null}>
        {p ? (
          <>
            <div className="tip-title">{formatDate(p.as_of, { year: 'always' })}</div>
            <div className="tip-total">{formatMoney(p.balance)} in the HSA</div>
            <div className="tip-row">
              <i style={{ background: 'var(--s1)' }} />
              <span>Contributed</span>
              <span>{formatMoney(p.balance - p.growth)}</span>
            </div>
            <div className="tip-row">
              <i style={{ background: 'var(--s5)' }} />
              <span>Growth</span>
              <span>{formatMoney(p.growth)}</span>
            </div>
          </>
        ) : null}
      </Tip>
    </div>
  );
}
